/**
 * ## Socket Enabler Client
 * Implements a custom data transport layer. It creates a network provider that can be used to subscribe and fetch data from the endpoint.
 *
 * Uses [sockjs](https://github.com/sockjs) which enables fallback when websocket is not supported, and then uses http long polling.
 * Messaging protocol used is [stomp-websocket](https://github.com/stomp-js/stompjs).
 *
 * ### Configuration
 *
 * This module enables configuration as following:
 *
 * | Parameter         | Type   | Default value         | Description |
 * | ---               | ---    | ---                   | --- |
 * | baseUrl           | String |                       | The base URL for the endpoint |
 * | destinationPrefix | String | /api                  | Prefix path for all endpoints |
 * | executionEngine   | ExecutionEngine | new ExecutionEngine() | Queue for sending requests via web socket. Used for communication between `SocketEnablerClient` and `Repository`. |
 *
 *
 * ### Examples
 *
 * #### Interface
 *     import { SocketEnablerClient } from '@pbs/ngen-assets';
 *
 * #### Get network provider pointing to endpoint "items"
 *     const socketEnablerClient = new SocketEnablerClient();
 *     const networkProvider = socketEnablerClient.createNetworkProvider('items');
 *
 * @module module:modules/socket-enabler-client
 *
 * @public
 */

import { x, subscribable } from '../utils';
import Client from './client';
import ResponseBus from './response-bus';

import Pubsub from './pubsub';
import Request from './request';
import ExecutionEngine from '../execution-engine';
import NetworkProvider from './network-provider';

export default function SocketEnablerClient() {
    const channel = subscribable.mixin({});
    const channelName = 'CONNECTION_STATUS';

    let pubsub;
    let request;
    let destinationPrefix;

    let wasInitialized = false;
    let executionEngine;
    let client;

    /**
     * Initializes Socket connection.
     *
     * @example
     * const socketEnablerClient = new SocketEnablerClient();
     * executionEngine = new ExecutionEngine();
     * socketEnablerClient.init({
     *    baseUrl: 'http://localhost:8080'
     *    destinationPrefix: '/api',
     *    executionEngine: executionEngine,
     * });
     *
     * @param {Object} config Configuration properties. Optional
     *
     * @return {Object} SocketEnablerClient instance with applied configuration
     *
     * @memberof module:modules/socket-enabler-client
     *
     * @function init
     *
     * @public
     */
    const init = config => {
        config = config || {};
        config.baseUrl = config.baseUrl || '';
        executionEngine = config.executionEngine || new ExecutionEngine();
        destinationPrefix = config.destinationPrefix = config.destinationPrefix || '/api';
        client = new Client();
        client.init(config);
        const bus = new ResponseBus(client);
        request = new Request(client, bus);
        pubsub = new Pubsub(client, bus);

        client.onError(message => channel.publish(channelName, { type: 'DISCONNECTED', payload: { message } }));
        client.onConnect(() => channel.publish(channelName, { type: 'CONNECTED' }));
        client.onWSConnectionFailure(() =>
            channel.publish(channelName, { type: 'WS_CONNECTION_FAILURE' })
        );
        client.onReady(() => channel.publish(channelName, { type: 'READY' }));
        client.onConnecting(() => channel.publish(channelName, { type: 'CONNECTING' }));
        client.onClose(event => channel.publish(channelName, { type: 'CLOSED', payload: { event }}));
        client.onSubscribe(payload => channel.publish(channelName, { type: 'SUBSCRIBE', payload }));

        wasInitialized = true;
        return this;
    };

    /**
     * Terminates client connection.
     *
     * @memberof module:modules/socket-enabler-client
     *
     * @function terminate
     *
     * @instance
     *
     * @public
     */
    const terminate = () => {
        client.disconnect();
    };

    /**
     * Subscribes to Socket connection events and gets an update on connect and disconnect events
     *
     * Callback used by subscribeToConnectionEvent is called with an event object with property ``type``
     * (CONNECTED/DISCONNECTED) and additional ``message`` property in case of disconnect event
     *
     * @example
     * socketEnablerClient.subscribeToConnectionEvent(function (event) {
     *     // use event
     * });
     *
     * @param {function} callback - The callback that handles the event change
     *
     * @return {Object} subscription object with a dispose function to dispose the subscription
     *
     * @memberof module:modules/socket-enabler-client
     *
     * @function subscribeToConnectionEvent
     *
     * @instance
     *
     * @public
     */
    const subscribeToConnectionEvent = callback => {
        assertInitialized();
        return { dispose: channel.subscribe(channelName, callback).dispose };
    };

    /**
     * Checks of the socket connection status
     *
     * @return {Boolean} true if the connection is active, false otherwise
     *
     * @memberof module:modules/socket-enabler-client
     *
     * @function isConnected
     *
     * @instance
     *
     * @public
     */
    const isConnected = () => {
        assertInitialized();
        return client.isConnected();
    };

    const start = () => {
        assertInitialized();
        client.connect();
    };

    const instrumentation = () => ({
        __this__: true,
        __name__: 'Socket enabler',
        pubsub: pubsub,
        request: request,
        destinationPrefix: destinationPrefix,
    });

    function getDestination(config) {
        let destination = destinationPrefix;
        if (config.locale) {
            destination = join(destination, config.locale);
        }
        destination = join(destination, config.name);
        if (config.id) {
            destination = join(destination, config.id);
        }
        return destination;
    }

    function join(first, second) {
        const result = `${first}/${second}`;
        return result.replace(/\/+/, '/');
    }

    function resolveVersionTagCallback(versionTag) {
        if (x.isFunction(versionTag)) {
            return versionTag;
        }
        return cb => cb(versionTag);
    }

    const subscribe = (config, onSubscribe, onUpdate) => {
        if (!config.id) {
            throw new Error('attribute "id" is required');
        }

        const destination = getDestination(config);
        const versionTag = config.versionTag;
        const headerLocale = config.headerLocale;
        const provideVersionTag = resolveVersionTagCallback(versionTag);

        if (config.bypassQueue) {
            return pubsub.subscribe(
                destination,
                headerLocale,
                provideVersionTag,
                onSubscribe,
                onUpdate,
                config.id,
                config.name
            );
        }
        return subscribeViaQueue();

        function subscribeViaQueue() {
            let isDisposed = false;
            let subscription = null;

            const job = done => {
                const tappedOnSubscribe = (err, type, data) => {
                    done();
                    onSubscribe(err, type, data);
                };

                // Abort subscription on early dispose call
                if (isDisposed) {
                    done();
                } else {
                    subscription = pubsub.subscribe(
                        destination,
                        headerLocale,
                        provideVersionTag,
                        tappedOnSubscribe,
                        onUpdate,
                        config.id,
                        config.name
                    );
                }
            };

            executionEngine.addJob(config.name, config.id || '', {
                job: job,
                info: 'SE-SUBSCRIBE',
            });

            return {
                dispose: () => {
                    if (subscription) {
                        executionEngine.addJob(config.name, config.id || '', {
                            job: subscription.dispose,
                            info: 'SE-DISPOSE',
                        });
                    } else {
                        isDisposed = true;
                    }
                },
            };
        }
    };

    const subscribeMultiple = (config, onSubscribe, onUpdate) => {
        if (!config?.ids.length) {
            throw new Error('attribute "ids" is required');
        }

        const destination = `${getDestination(config)}`;
        const versionTag = config.versionTag;
        const headerLocale = config.headerLocale;
        const provideVersionTag = resolveVersionTagCallback(versionTag);

        if (config.bypassQueue) {
            return pubsub.subscribeMultiple(
                destination,
                config.ids,
                headerLocale,
                provideVersionTag,
                onSubscribe,
                onUpdate,
                config.name
            );
        }

        function subscribeViaQueue() {
            let isDisposed = false;
            let subscription = null;

            const job = done => {
                const tappedOnSubscribe = (err, type, data) => {
                    done();
                    onSubscribe(err, type, data);
                };

                // Abort subscription on early dispose call
                if (isDisposed) {
                    done();
                } else {
                    subscription = pubsub.subscribeMultiple(
                        destination,
                        config.ids,
                        headerLocale,
                        provideVersionTag,
                        tappedOnSubscribe,
                        onUpdate,
                        config.name
                    );
                }
            };

            executionEngine.addJob(config.name, config.id || '', {
                job: job,
                info: 'SE-SUBSCRIBE',
            });

            return {
                dispose: () => {
                    if (subscription) {
                        executionEngine.addJob(config.name, config.ids || '', {
                            job: subscription.dispose,
                            info: 'SE-DISPOSE',
                        });
                    } else {
                        isDisposed = true;
                    }
                },
            };
        }

        return subscribeViaQueue();
    };

    const fetch = (config, callback, headers) => {
        if (config.bypassQueue) {
            request.get(getDestination(config), callback, headers, undefined, config.headerLocale);
        } else {
            makeRequestViaQueue(config, undefined, callback, headers);
        }
    };

    const put = (config, json, callback, headers) => {
        if (config.bypassQueue) {
            request.get(
                getDestination(config),
                callback,
                headers,
                JSON.stringify(json),
                config.headerLocale
            );
        } else {
            makeRequestViaQueue(config, json, callback, headers);
        }
    };

    function makeRequestViaQueue(config, json, callback, headers) {
        const hasPayload = json !== undefined;
        const job = done =>
            request.get(
                getDestination(config),
                (err, data, headers2) => {
                    done();
                    callback(err, data, headers2);
                },
                headers,
                hasPayload ? JSON.stringify(json) : undefined,
                config.headerLocale
            );

        executionEngine.addJob(config.name, config.id || '', {
            job: job,
            info: hasPayload ? 'SE-PUT' : 'SE-FETCH',
        });
    }

    function assertInitialized() {
        if (!wasInitialized) {
            throw new Error('Init method of the "socket-enabler" component was not called.');
        }
    }

    /**
     * Defines an endpoint and returns a network provider for further interaction.
     *
     * Returned NetworkProvider has two functions that is normally used:
     *
     * * fetch {Function} Initial fetching of endpoint data
     * * subscribe {Function} Subscribe to endpoint data
     *
     * @param {String} endpointName Endpoint name (similar to mongoDB collection name)
     * @param {Object} config Configuration object. Optional
     *
     * @property {String} config.idKey defines field which acts as id in the endpoint. Default is "id"
     *
     * @return {Object} NetworkProvider instance for current endpoint
     *
     * @memberof module:modules/socket-enabler-client
     *
     * @function createNetworkProvider
     *
     * @instance
     *
     * @public
     */
    const createNetworkProvider = (endpointName, config) => {
        assertInitialized();
        if (!endpointName) {
            throw new Error('Missing endpointName parameter');
        }

        return new NetworkProvider(endpointName, fetch, subscribe, subscribeMultiple, config);
    };

    // Expose methods
    this.init = init;
    this.terminate = terminate;
    this.subscribeToConnectionEvent = subscribeToConnectionEvent;
    this.isConnected = isConnected;
    this.start = start;
    this.instrumentation = instrumentation;
    this.subscribe = subscribe;
    this.subscribeMultiple = subscribeMultiple;
    this.fetch = fetch;
    this.put = put;
    this.createNetworkProvider = createNetworkProvider;

    // For testing
    this.getClient = () => client;
}
